import { registerSW } from 'virtual:pwa-register'

/**
 * Self-updating: the service worker is registered in autoUpdate mode, so when a new build is
 * found the new worker takes over and the page reloads. Browsers only look for a new worker on
 * navigation and about once a day, which an installed PWA rarely does. So the app also compares
 * the server's revision (in the manifest) with its own and asks for an update check when they
 * differ, plus a routine check every hour while visible.
 */
export const APP_VERSION = __APP_VERSION__
/** When that revision was committed, ISO 8601; empty in a build that had no git and no VERSION file */
export const APP_DATE = __APP_DATE__
/** What changed in the versions up to this one, newest first: the commit subjects */
export const APP_CHANGES = __APP_CHANGES__

let registration: ServiceWorkerRegistration | undefined
let checking = false

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return
  registerSW({
    immediate: true,
    onRegisteredSW(_url, reg) {
      registration = reg
      window.setInterval(() => {
        if (document.visibilityState === 'visible') void checkForUpdate()
      }, 60 * 60_000)
    }
  })
}

/** Ask the browser to fetch sw.js again; a changed worker installs and, in autoUpdate mode, reloads the page */
export async function checkForUpdate(): Promise<void> {
  if (!registration || checking || !navigator.onLine) return
  checking = true
  try {
    await registration.update()
  } catch {
    // offline or the server is down; the next check will try again
  } finally {
    checking = false
  }
}

/** Called with the server's revision after every manifest fetch */
export function noteServerVersion(serverVersion: string | undefined): boolean {
  const differs = !!serverVersion && serverVersion !== APP_VERSION && APP_VERSION !== 'dev'
  if (differs) void checkForUpdate()
  return differs
}
